import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ShieldCheckIcon, BuildingOffice2Icon, CreditCardIcon, CodeBracketIcon, BookOpenIcon, SunIcon, MoonIcon,
} from '@heroicons/react/24/outline'
import { API_BASE_URL } from '../../config/api'
import { csrfHeader, clearCsrfToken } from '../../lib/csrf'
import { getTheme, toggleTheme } from '../../theme'
import { DashRoot, IconButton, Avatar } from '../dashboard/primitives'
import { SidebarLayout, type SideSection } from '../dashboard/Layout'

export type AdminRole = 'admin' | 'reviewer' | 'platform'

export const ROLE_LABEL: Record<AdminRole, string> = { platform: 'Platform admin', admin: 'Organisation admin', reviewer: 'Reviewer' }

/** Admin area shell: white sidebar variant with grouped nav + log out. */
export function AdminShell({ role, reviewBadge, children }: { role: AdminRole; reviewBadge?: number; children: React.ReactNode }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [theme, setTheme] = React.useState(getTheme())

  const logout = () => {
    fetch(`${API_BASE_URL}/api/auth/logout`, { method: 'POST', credentials: 'include', headers: csrfHeader() }).catch(() => {})
    clearCsrfToken()
    navigate('/admin/login')
  }

  const sections: SideSection[] = [
    {
      label: 'General',
      items: [
        { label: 'Verifications', to: '/admin/verifications', icon: <ShieldCheckIcon />, badge: reviewBadge },
        ...(role === 'platform' ? [
          { label: 'Organisations', to: '/admin/developers', icon: <BuildingOffice2Icon /> },
          { label: 'Billing', to: '/admin/billing', icon: <CreditCardIcon /> },
        ] : []),
      ],
    },
    {
      label: 'Tools',
      items: [
        { label: 'Developer portal', to: '/developer', icon: <CodeBracketIcon /> },
        { label: 'Documentation', to: '/docs/review', icon: <BookOpenIcon /> },
      ],
    },
  ]

  const themeBtn = (
    <IconButton label={theme === 'dark' ? 'Light theme' : 'Dark theme'} onClick={() => setTheme(toggleTheme())}>
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </IconButton>
  )

  return (
    <DashRoot>
      <SidebarLayout
        sections={sections}
        pathname={pathname}
        onLogout={logout}
        topRight={themeBtn}
        footer={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 10px 12px' }}>
            <Avatar name={ROLE_LABEL[role]} size={36} tone={1} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{ROLE_LABEL[role]}</div>
              <div style={{ fontSize: 12, color: 'var(--sf-mid)' }}>Review dashboard</div>
            </div>
            {themeBtn}
          </div>
        }
      >
        {children}
      </SidebarLayout>
    </DashRoot>
  )
}
